import type { CastAssignment, Personnel } from '../personnel/types';
import type { Act, Character, Scene } from '../show-structure/types';
import { getDefaultTemplateRehearsalAttendeeIds, isTemplateRehearsalEventType } from './attendee-defaults';
import { expandScheduleEventAttendeeIds } from './attendees';
import type { RehearsalScene, ScheduleEvent } from './types';

export interface ScheduleCallResolutionContext {
    acts: Pick<Act, 'id' | 'sceneIds'>[];
    scenes: Pick<Scene, 'id' | 'deleted'>[];
    characters: Character[];
    rehearsalScenes: Pick<RehearsalScene, 'id' | 'characterIds' | 'relatedSceneIds'>[];
    castAssignments: CastAssignment[];
    personnel: Personnel[];
}

export type ScheduleCallResolutionSource = 'saved' | 'derived' | 'none';

export interface ScheduleCallResolution {
    attendeeIds: string[];
    isTemplateCall: boolean;
    source: ScheduleCallResolutionSource;
    usesSavedAttendees: boolean;
}

export function shouldUseSavedScheduleAttendees(event: ScheduleEvent): boolean {
    const hasUnlockedTemplateCallBasis = isTemplateRehearsalEventType(event.type)
        && event.callBasis?.mode === 'unlocked';

    return Array.isArray(event.attendees)
        && !hasUnlockedTemplateCallBasis
        && (event.attendees.length > 0 || Boolean(event.callBasis));
}

export function resolveScheduleCallAttendees(
    event: ScheduleEvent,
    context: ScheduleCallResolutionContext
): ScheduleCallResolution {
    const isTemplateCall = isTemplateRehearsalEventType(event.type);

    if (shouldUseSavedScheduleAttendees(event)) {
        return {
            attendeeIds: expandScheduleEventAttendeeIds(event.attendees ?? [], context.personnel, {
                characters: context.characters,
                castAssignments: context.castAssignments,
            }),
            isTemplateCall,
            source: 'saved',
            usesSavedAttendees: true,
        };
    }

    if (!isTemplateCall) {
        return {
            attendeeIds: [],
            isTemplateCall,
            source: 'none',
            usesSavedAttendees: false,
        };
    }

    return {
        attendeeIds: getDefaultTemplateRehearsalAttendeeIds({
            eventType: event.type,
            targetId: event.targetId,
            characters: context.characters,
            acts: context.acts,
            rehearsalScenes: context.rehearsalScenes,
            scenes: context.scenes,
            castAssignments: context.castAssignments,
            personnel: context.personnel,
        }),
        isTemplateCall,
        source: 'derived',
        usesSavedAttendees: false,
    };
}

export function getResolvedScheduleCallAttendeeIds(
    event: ScheduleEvent,
    context: ScheduleCallResolutionContext
): string[] {
    return resolveScheduleCallAttendees(event, context).attendeeIds;
}
